<script setup>
import Dialog from "primevue/dialog";
import { ref, computed, watch } from "vue";
import { useUserStore } from "@/store/user-store";
import AnButton from "@/components/common/AnButton.vue";

const store = useUserStore();

// Props and Emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  mode: {
    type: String,
    default: "ADD", // "ADD" or "WITHDRAW"
    validator: (value) => ["ADD", "WITHDRAW"].includes(value)
  },
  userHoldings: {
    type: Array,
    default: () => []
  },
  selectedInstrument: {
    type: Object,
    default: null
  },
  prefilledData: {
    type: Object,
    default: null
  }
});
const emit = defineEmits(["update:modelValue", "update:mode"]);

// Form data
const investmentType = ref(null);
const amountInvested = ref("");
const investedDate = ref("");
const currentValue = ref("");

// Investment type options
const investmentTypes = [
  { label: 'Gold', value: 'Gold' },
  { label: 'Real Estate', value: 'Real Estate' },
  { label: 'Others', value: 'Others' }
];

// Dynamic investment type list based on mode
const investmentTypeList = computed(() => {
  if (props.mode === "WITHDRAW") {
    console.log("Raw userHoldings:", props.userHoldings);
    const mapped = props.userHoldings
      .filter(holding => (holding.currentVal || 0) > 0) // Filter out zero current value
      .map(holding => ({
        label: holding.investmentType,
        value: `${holding.investmentType}_${holding.itemId}`, // Use unique identifier
        id: holding.itemId.toString(),
        currentValue: holding.currentVal || 0,
        amountInvested: holding.amountInvested,
        itemId: holding.itemId,
        investedDate: holding.investedDate,
        // Keep original data for reference
        originalInvestmentType: holding.investmentType
      }));
    console.log("Mapped investmentTypeList (filtered):", mapped);
    return mapped;
  }
  return investmentTypes;
});

// Get max withdrawal amount (100% of current value for other investments)
const maxWithdrawalAmount = computed(() => {
  if (props.mode === "WITHDRAW" && investmentType.value) {
    const selectedInstrument = investmentTypeList.value.find(item => item.value === investmentType.value);
    if (selectedInstrument) {
      return Math.floor(selectedInstrument.currentValue);
    }
  }
  return 0;
});

// Get selected instrument details
const selectedInstrumentDetails = computed(() => {
  if (props.mode === "WITHDRAW" && investmentType.value) {
    console.log("Looking for investmentType.value:", investmentType.value);
    console.log("Available options:", investmentTypeList.value);
    const found = investmentTypeList.value.find(item => item.value === investmentType.value);
    console.log("Found instrument details:", found);
    return found;
  }
  return null;
});

// Validation and error handling
const errors = ref({});
const isLoading = ref(false);

// Watch for dialog open/close to reset fields
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    // Reset all fields when dialog opens
    investmentType.value = null;
    amountInvested.value = "";
    investedDate.value = getTodayDate();
    currentValue.value = "";
    errors.value = {};
    isLoading.value = false;

    // Handle prefilled data if provided
    if (props.prefilledData) {
      console.log('Prefilled data received:', props.prefilledData);

      // Set investment type based on prefilled data
      if (props.prefilledData.investmentType) {
        investmentType.value = props.prefilledData.investmentType;
      }

      // Set amount invested (rounded to 2 decimal places)
      if (props.prefilledData.amountInvested) {
        amountInvested.value = Math.round(props.prefilledData.amountInvested).toString();
      }

      // Set current value (same as amount invested for initial setup)
      if (props.prefilledData.currentValue) {
        currentValue.value = Math.round(props.prefilledData.currentValue).toString();
      }

      // Set invested date
      if (props.prefilledData.investedDate) {
        investedDate.value = props.prefilledData.investedDate;
      }

      console.log('Form populated with prefilled data:', {
        investmentType: investmentType.value,
        amountInvested: amountInvested.value,
        currentValue: currentValue.value,
        investedDate: investedDate.value
      });
    }
  }
});

// Watch for investment type selection to auto-populate withdrawal amount
watch(investmentType, (newValue) => {
  if (props.mode === "WITHDRAW" && newValue) {
    const selectedInstrument = investmentTypeList.value.find(item => item.value === newValue);
    if (selectedInstrument) {
      // For Real Estate - only full liquidation allowed
      if (selectedInstrument.originalInvestmentType === 'Real Estate') {
        amountInvested.value = Math.floor(selectedInstrument.amountInvested).toString();
        currentValue.value = Math.floor(selectedInstrument.currentValue).toString();
      } else {
        // For other investments - auto-populate with full amount but allow partial
        amountInvested.value = Math.floor(selectedInstrument.amountInvested).toString();
        currentValue.value = Math.floor(selectedInstrument.currentValue).toString();
      }
    }
  }
});

// Watch for liquidation amount changes to auto-calculate liquidation value
watch(amountInvested, (newAmount) => {
  if (props.mode === "WITHDRAW" && selectedInstrumentDetails.value && newAmount) {
    const selected = selectedInstrumentDetails.value;
    const amountValue = parseFloat(newAmount);

    // Skip auto-calculation for Real Estate (full liquidation only)
    if (selected.originalInvestmentType === 'Real Estate') {
      return;
    }

    if (selected.amountInvested > 0 && amountValue > 0) {
      // Calculate proportional liquidation value
      const percentage = amountValue / selected.amountInvested;
      const proportionalValue = Math.floor(selected.currentValue * percentage);
      currentValue.value = proportionalValue.toString();
    }
  }
});

// Computed properties
const isFieldsEnabled = computed(() => {
  return investmentType.value !== null;
});

const dialogTitle = computed(() => {
  if (props.mode === "WITHDRAW") {
    return "Liquidate Other Investment";
  }
  return "Add Other Investment";
});

// Check if selected investment is Real Estate
const isRealEstateInvestment = computed(() => {
  return selectedInstrumentDetails.value && selectedInstrumentDetails.value.originalInvestmentType === 'Real Estate';
});

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get date 5 years ago in YYYY-MM-DD format
const getFiveYearsAgoDate = () => {
  const today = new Date();
  const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate());
  return fiveYearsAgo.toISOString().split('T')[0];
};

// Format numbers in Indian format (Lakhs/Crores)
const formatIndianCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num >= 10000000) { // 1 crore and above
    return `₹${(num / 10000000).toFixed(1)} Cr`;
  } else if (num >= 100000) { // 1 lakh and above
    return `₹${(num / 100000).toFixed(1)} L`;
  } else if (num >= 1000) { // 1 thousand and above
    return `₹${(num / 1000).toFixed(0)}K`;
  } else {
    return `₹${num.toLocaleString('en-IN')}`;
  }
};

// Set default invested date to today
investedDate.value = getTodayDate();

// Handle mode switching
const switchMode = (newMode) => {
  emit("update:mode", newMode);
  // Reset form when switching modes
  resetForm();
};

// Reset form function
const resetForm = () => {
  investmentType.value = null;
  amountInvested.value = "";
  investedDate.value = getTodayDate();
  currentValue.value = "";
  errors.value = {};
  isLoading.value = false;
};

// Validation function
const validateForm = () => {
  errors.value = {};

  if (!investmentType.value) {
    errors.value.investmentType = 'Investment type is required';
  }

  if (props.mode === "WITHDRAW") {
    // Validation for WITHDRAW mode
    if (!amountInvested.value || parseFloat(amountInvested.value) <= 0) {
      errors.value.amountInvested = 'Withdrawal amount must be greater than 0';
    }

    if (parseFloat(amountInvested.value) > maxWithdrawalAmount.value) {
      errors.value.amountInvested = `Cannot withdraw more than ₹${maxWithdrawalAmount.value.toLocaleString()}`;
    }

    if (!currentValue.value || parseFloat(currentValue.value) <= 0) {
      errors.value.currentValue = 'Current value must be greater than 0';
    }
  } else {
    // Validation for ADD mode (existing logic)
    if (!amountInvested.value || parseFloat(amountInvested.value) < 100) {
      errors.value.amountInvested = 'Amount invested must be at least ₹100';
    }

    if (parseFloat(amountInvested.value) > 50000000) {
      errors.value.amountInvested = 'Amount invested cannot exceed ₹5 crores';
    }

    if (!currentValue.value || parseFloat(currentValue.value) < 100) {
      errors.value.currentValue = 'Current value must be at least ₹100';
    }

    if (parseFloat(currentValue.value) > 100000000) {
      errors.value.currentValue = 'Current value cannot exceed ₹10 crores';
    }
  }

  if (!investedDate.value) {
    errors.value.investedDate = 'Invested date is required';
  }

  return Object.keys(errors.value).length === 0;
};

// Add other investment function
const addOtherInvestment = async () => {
  if (!validateForm()) {
    return;
  }

  isLoading.value = true;

  try {
    let payload;

    if (props.mode === "WITHDRAW") {
      // WITHDRAW mode payload
      const selectedDetails = selectedInstrumentDetails.value;
      payload = {
        itemId: selectedDetails.itemId,
        transactionType: "SELL",
        investmentType: selectedDetails.originalInvestmentType || selectedDetails.label,
        investedDate: investedDate.value,
        amountInvested: -Math.abs(parseFloat(amountInvested.value)), // Negative for withdrawal
        currentValue: -Math.abs(parseFloat(currentValue.value)) // Negative for withdrawal
      };

      console.log("WITHDRAW payload being sent to API:", payload);
      console.log("Selected instrument details:", selectedDetails);
    } else {
      // ADD mode payload (existing logic)
      payload = {
        transactionType: "ADD",
        investmentType: investmentType.value,
        investedDate: investedDate.value,
        amountInvested: parseFloat(amountInvested.value),
        currentValue: parseFloat(currentValue.value)
      };

      console.log("ADD payload being sent to API:", payload);
    }

    await store.addOtherInvestments(payload);

    // Close dialog on success
    closeDialog();

  } catch (error) {
    console.error('Error processing other investment:', error);
    errors.value.submit = `Failed to ${props.mode.toLowerCase()} other investment. Please try again.`;
  } finally {
    isLoading.value = false;
  }
};

// Close dialog function
const closeDialog = () => {
  emit("update:modelValue", false);
};
</script>

<template>
  <Dialog
    modal
    :visible="modelValue"
    :closable="false"
    :dismissable-mask="false"
    @update:visible="(value) => emit('update:modelValue', value)"
  >
    <template #header>
      <div class="d-flex align-items-center position-relative w-100 p-20 dialog-header">
        <!-- Mode Toggle Buttons - Left Top Corner -->
        <div class="mode-toggle-container-left">
          <AnButton
            label="Add"
            :variant="props.mode === 'ADD' ? 'primary' : 'tab'"
            @click="switchMode('ADD')"
            class="mode-toggle-btn-small"
          />
          <AnButton
            label="Liquidate"
            :variant="props.mode === 'WITHDRAW' ? 'primary' : 'tab'"
            @click="switchMode('WITHDRAW')"
            class="mode-toggle-btn-small"
          />
        </div>

        <span class="text-center text-primary w-100 font-20 font-weight-700">
          {{ dialogTitle }}
        </span>
        <i
          @click="closeDialog"
          class="pi pi-times bg-error text-white p-5 br-5 position-absolute r-0 mr-10 cursor-pointer"
        />
      </div>
    </template>

    <div class="ph-30 pb-30">
      <!-- Step 1: Investment Type Selection -->
      <div class="step-section">
        <div class="step-header">
          <div class="step-indicator">
            <span class="step-number">1</span>
          </div>
          <span class="step-title">Investment Type Selection</span>
        </div>

        <div class="d-grid investment-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="investmentType" class="font-weight-600">
              {{ props.mode === 'WITHDRAW' ? 'Select Investment to Liquidate' : 'Investment Type' }}
            </label>
            <select
              id="investmentType"
              v-model="investmentType"
              class="input-field"
              :class="{ 'border-red-500': errors.investmentType, 'placeholder-select': !investmentType }"
            >
              <option value="" disabled selected hidden>
                {{ props.mode === 'WITHDRAW' ? 'Select investment to liquidate' : 'Select investment type' }}
              </option>
              <option
                v-for="type in investmentTypeList"
                :key="type.id || type.value"
                :value="type.value"
              >
                {{ type.originalInvestmentType || type.label }}
                <span v-if="props.mode === 'WITHDRAW'">
                  (Current: {{ formatIndianCurrency(type.currentValue || 0) }})
                </span>
              </option>
            </select>
            <small v-if="errors.investmentType" class="error-msg">{{ errors.investmentType }}</small>
          </div>

          <!-- Show current value and withdrawal limits for WITHDRAW mode -->
          <div v-if="props.mode === 'WITHDRAW' && selectedInstrumentDetails" class="d-flex flex-direction-column">
            <label class="font-weight-600">Current Value & Withdrawal Limits</label>
            <div class="info-box">
              <div class="info-row">
                <span>Current Value:</span>
                <span class="font-weight-600">{{ formatIndianCurrency(selectedInstrumentDetails.currentValue || 0) }}</span>
              </div>
              <div class="info-row">
                <span>Max Withdrawal:</span>
                <span class="font-weight-600 text-primary">{{ formatIndianCurrency(maxWithdrawalAmount) }}</span>
              </div>
              <div v-if="isRealEstateInvestment" class="note-text real-estate-note">
                <i class="pi pi-home text-primary"></i>
                <span><strong>Real Estate:</strong> Only full liquidation allowed. You cannot sell half of a property.</span>
              </div>
              <div v-else class="note-text">
                <i class="pi pi-info-circle text-primary"></i>
                <div>
                  <div><strong>Partial Liquidation:</strong> Enter portion of original investment amount</div>
                  <div><strong>Auto-calculated:</strong> Liquidation value will be proportionally calculated</div>
                  <div class="text-sm mt-5">Example: Liquidate 40% of ₹{{ Math.floor(selectedInstrumentDetails.amountInvested || 0).toLocaleString() }} = Receive ₹{{ Math.floor((selectedInstrumentDetails.currentValue || 0) * 0.4).toLocaleString() }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Investment Details -->
      <div class="step-section" :class="{ 'disabled-section': !isFieldsEnabled }">
        <div class="step-header">
          <div class="step-indicator" :class="{ 'active': isFieldsEnabled }">
            <span class="step-number">2</span>
          </div>
          <span class="step-title">Investment Details</span>
        </div>

        <div class="d-grid transaction-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="amountInvested" class="font-weight-600">
              {{ props.mode === 'WITHDRAW' ? 'Liquidation Amount (₹)' : 'Amount Invested (₹)' }}
            </label>
            <input
              id="amountInvested"
              v-model="amountInvested"
              type="text"
              :disabled="!isFieldsEnabled"
              :readonly="props.mode === 'WITHDRAW' && isRealEstateInvestment"
              :placeholder="props.mode === 'WITHDRAW' ?
                `Enter liquidation amount (max ₹${maxWithdrawalAmount.toLocaleString()})` :
                'Enter amount invested'"
              class="input-field"
              :class="{
                'border-red-500': errors.amountInvested,
                'readonly-field': props.mode === 'WITHDRAW' && isRealEstateInvestment
              }"
              @input="amountInvested = $event.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
            />
            <small v-if="errors.amountInvested" class="error-msg">{{ errors.amountInvested }}</small>
            <small class="helper-text">
              {{ props.mode === 'WITHDRAW' ?
                (isRealEstateInvestment ? 'Full liquidation required for Real Estate' : `Range: ₹1 - ₹${maxWithdrawalAmount.toLocaleString()}`) :
                'Range: ₹100 - ₹5,00,00,000' }}
            </small>
          </div>

          <div class="d-flex flex-direction-column">
            <label for="currentValue" class="font-weight-600">
              {{ props.mode === 'WITHDRAW' ? 'Liquidation Value (₹)' : 'Current Value (₹)' }}
            </label>
            <input
              id="currentValue"
              v-model="currentValue"
              type="text"
              :disabled="!isFieldsEnabled"
              :readonly="props.mode === 'WITHDRAW' && isRealEstateInvestment"
              :placeholder="props.mode === 'WITHDRAW' ?
                'Enter liquidation value' :
                'Enter current value'"
              class="input-field"
              :class="{
                'border-red-500': errors.currentValue,
                'readonly-field': props.mode === 'WITHDRAW' && isRealEstateInvestment
              }"
              @input="currentValue = $event.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
            />
            <small v-if="errors.currentValue" class="error-msg">{{ errors.currentValue }}</small>
            <small class="helper-text">
              {{ props.mode === 'WITHDRAW' ?
                (isRealEstateInvestment ? 'Full property value (auto-calculated)' : 'Value you will receive from liquidation') :
                'Range: ₹100 - ₹10,00,00,000' }}
            </small>
          </div>

          <div class="d-flex flex-direction-column">
            <label for="investedDate" class="font-weight-600">
              {{ props.mode === 'WITHDRAW' ? 'Liquidation Date' : 'Invested Date' }}
            </label>
            <input
              id="investedDate"
              v-model="investedDate"
              type="date"
              :disabled="!isFieldsEnabled"
              :min="getFiveYearsAgoDate()"
              :max="getTodayDate()"
              class="input-field"
              :class="{ 'border-red-500': errors.investedDate }"
            />
            <small v-if="errors.investedDate" class="error-msg">{{ errors.investedDate }}</small>
            <small class="helper-text">
              {{ props.mode === 'WITHDRAW' ?
                'Date of liquidation' :
                'Up to 5 years back date allowed' }}
            </small>
          </div>
        </div>
      </div>

      <!-- Submit Error -->
      <div v-if="errors.submit" class="error-section">
        <p class="text-red-600 text-sm">{{ errors.submit }}</p>
      </div>
    </div>

    <template #footer>
      <div class="d-flex justify-center gap-20 w-100 p-10">
        <AnButton
          label="Back"
          variant="tab"
          @click="closeDialog"
          :disabled="isLoading"
        />
        <AnButton
          :label="props.mode === 'WITHDRAW' ? 'Liquidate' : 'Add'"
          variant="primary"
          :disabled="!isFieldsEnabled || isLoading"
          @click="addOtherInvestment"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.dialog-header {
  border-bottom: 2px solid;
  border-bottom-color: var(--an-primary);
}

/* Mode Toggle Styling - Left Top Corner */
.mode-toggle-container-left {
  position: absolute;
  left: 20px;
  top: 20px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

.mode-toggle-btn-small {
  min-width: 60px !important;
  font-size: 12px !important;
  padding: 6px 12px !important;
  height: 32px;
}

/* Step Layout Styling */
.step-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.step-section.disabled-section {
  opacity: 0.6;
  background: #f1f5f9;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.step-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #cbd5e1;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step-indicator.active {
  background: var(--an-primary);
}

.step-number {
  font-size: 12px;
}

.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

/* Grid Layouts */
.investment-grid {
  grid-template-columns: 1fr;
  max-width: 100%;
}

.transaction-grid {
  grid-template-columns: repeat(2, 1fr);
  max-width: 100%;
}

/* Info Box Styling for WITHDRAW mode */
.info-box {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.info-row:last-of-type {
  margin-bottom: 0;
}

.note-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #374151;
  margin-top: 8px;
  padding: 8px;
  background: #fef3c7;
  border-radius: 6px;
  border: 1px solid #f59e0b;
}

.input-field {
  padding: 16px;
  border: 2px solid var(--an-gray1);
  border-radius: 5px;
  font-size: 14px;
  width: 100%;
  max-width: 280px;
}

.input-field:focus {
  outline: none;
  border-color: var(--an-primary);
}

.input-field.border-red-500 {
  border-color: #ef4444;
}

.input-field.placeholder-select {
  color: #9ca3af;
}

.input-field option {
  color: #000000;
}

.readonly-field {
  background-color: #f3f4f6 !important;
  cursor: not-allowed;
  color: #6b7280 !important;
}

.error-msg {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.helper-text {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}

.error-section {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
  margin-top: 16px;
}

/* Dialog Size Styling */
:deep(.p-dialog) {
  width: 35vw !important;
  max-width: 650px !important;
  min-width: 500px !important;
}

:deep(.p-dialog .p-dialog-content) {
  padding: 0 !important;
}

@media (max-width: 700px) {
  :deep(.p-dialog) {
    width: 98vw !important;
    min-width: 0 !important;
    max-width: 100vw !important;
  }

  .ph-30.pb-30 {
    padding-left: 8px !important;
    padding-right: 8px !important;
    padding-bottom: 8px !important;
  }

  .step-section {
    padding: 12px 6px 12px 6px;
    margin-bottom: 12px;
  }

  .d-grid.investment-grid, .d-grid.transaction-grid {
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
  }

  .input-field {
    font-size: 1rem;
    padding: 12px;
    max-width: 100%;
  }

  .dialog-header span {
    font-size: 1.2rem !important;
  }

  .step-title, .font-weight-600 {
    font-size: 1rem !important;
  }

  .d-flex.justify-center.gap-20.w-100.p-10 {
    flex-direction: column;
    gap: 12px;
    padding: 0 0 12px 0;
  }
}
</style>
